import {
  faAngleDown,
  faXmark
} from '@awesome.me/kit-5de77b2c02/icons/classic/solid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cx from 'classnames'
import { FC, useCallback, useMemo, useRef } from 'react'
import { noop } from 'rxjs'
import { useDropDownMenu } from '../../helpers/hooks/useDropDownMenu'
import { getAllOrNoneStatus } from '../../store/helpers'
import Checkbox from '../Checkbox'
import DropdownList from '../DropdownList/DropdownList'
import GroupItem from './Groups/GroupItem'
import { ItemGroup } from './types'

import styles from './GenericDropdownMenu.module.scss'

interface ListItemProps {
  id: string
  label: string | React.ReactNode
  selected: boolean
  toggleSingleOption: () => void
  disabled?: boolean
}

export const ListItem = ({
  id,
  label,
  selected,
  disabled = false,
  toggleSingleOption
}: ListItemProps) => {
  return (
    <div className={styles.listItem}>
      <Checkbox
        locator={`column-${id}`}
        checked={selected}
        onChange={toggleSingleOption}
        disabled={disabled}
      >
        {label}
      </Checkbox>
    </div>
  )
}

interface SelectAllProps {
  status: 'all' | 'none' | 'some'
  changeStatus: () => void
}

export const SelectAll: FC<SelectAllProps> = ({ status, changeStatus }) => {
  return (
    <div className={styles.listItem}>
      <Checkbox
        locator={`column-all`}
        checked={status === 'all'}
        indeterminate={status === 'some'}
        onChange={changeStatus}
      >
        Select all
      </Checkbox>
    </div>
  )
}

export interface GenericDropdownMenuMultiProps {
  placeholder: string
  groups?: readonly ItemGroup[]
  selectedItems: string[]
  setSelectedItems(items: string[]): void
  options: string[]
  selectId: string
  className: string
  openRight: boolean
  dropdownLabel?: string
  active?: boolean
  onRemove?: () => void
  testId?: string
}

const GenericDropdownMenuMulti: FC<GenericDropdownMenuMultiProps> = ({
  placeholder,
  groups,
  selectedItems,
  setSelectedItems,
  options,
  selectId,
  className,
  openRight = false,
  dropdownLabel,
  active,
  onRemove,
  testId
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { displayMenu, setDisplayMenu } = useDropDownMenu(ref)

  const showOrHideDropdown = useCallback(() => {
    setDisplayMenu((value) => !value)
    setSelectedItems(selectedItems)
  }, [selectedItems, setSelectedItems, setDisplayMenu])

  const isItemSelected = useMemo(() => {
    return (colId: string) => selectedItems.includes(colId)
  }, [selectedItems])

  const toggleGroup = useCallback(
    (group: readonly string[]) => () => {
      setSelectedItems(
        group.every((item) => isItemSelected(item))
          ? selectedItems.filter((c) => !group.includes(c))
          : [...group, ...selectedItems.filter((c) => !group.includes(c))]
      )
    },
    [selectedItems, isItemSelected, setSelectedItems]
  )

  const toggleSingleOption = useCallback(
    (optionName: string) => () => {
      setSelectedItems(
        isItemSelected(optionName)
          ? selectedItems.filter((c) => c !== optionName)
          : [...selectedItems, optionName]
      )
    },
    [selectedItems, isItemSelected, setSelectedItems]
  )

  const allSelectedStatus = useMemo(() => {
    return getAllOrNoneStatus(options, (column) => isItemSelected(column))
  }, [options, isItemSelected])

  const changeSelectAllStatus = useCallback(() => {
    switch (allSelectedStatus) {
      case 'none':
      case 'some':
        setSelectedItems(options)
        break
      case 'all':
        setSelectedItems([])
        break
    }
  }, [allSelectedStatus, setSelectedItems, options])

  const getElements = useCallback(() => {
    return groups
      ? groups.map((group, index) => ({
          id: index,
          label: (
            <GroupItem
              id={group.label}
              groupName={group.label}
              values={group.values}
              selectedItems={selectedItems}
              toggleGroup={toggleGroup}
              toggleSingleOption={toggleSingleOption}
            />
          )
        }))
      : options.map((name) => ({
          id: name,
          label: (
            <ListItem
              id={name}
              label={name}
              selected={isItemSelected(name)}
              toggleSingleOption={toggleSingleOption(name)}
            />
          )
        }))
  }, [
    groups,
    options,
    selectedItems,
    isItemSelected,
    toggleGroup,
    toggleSingleOption
  ])

  return (
    <div ref={ref} className={styles.dropdownContainer}>
      <div
        className={cx(
          styles.dropdown,
          active && styles.dropdownActive,
          className
        )}
        onClick={showOrHideDropdown}
        data-testid={testId}
      >
        {dropdownLabel ? (
          <span>{dropdownLabel}</span>
        ) : (
          <span className={styles.dropdownValue}>{placeholder}</span>
        )}
        <FontAwesomeIcon icon={faAngleDown} />
        {onRemove && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            aria-label="Remove filter"
            title="Remove filter"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>
      {displayMenu && (
        <div className={cx(styles.menu, { [styles.menuRight]: openRight })}>
          <DropdownList
            onElementSelected={noop} // So the elements look clickable, even if we handle the click in the onChange event of the checkbox.
            elements={[
              {
                id: selectId,
                label: (
                  <SelectAll
                    status={allSelectedStatus}
                    changeStatus={changeSelectAllStatus}
                  />
                )
              }
            ]}
          />
          <DropdownList onElementSelected={noop} elements={getElements()} />
        </div>
      )}
    </div>
  )
}

export default GenericDropdownMenuMulti
